/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import {
  Box,
  Typography,
  Popover,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import Topheader from "../../../../Layout/header/header";
import Sidebar from "../sidebar/sidebar";
import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import "./portfolios.css";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import ApiManager from "../../../../Services/ApiManager/ApiManager";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.action.hover,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

function createData(name) {
  return { name };
}

export default function PortfolioSettings() {
  const [loader, setLoader] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const popupContent = "";
  const popupTitle = "Details";
  const selectedPortfolio = "";
  const [dataFromChild, setDataFromChild] = useState("");
  const allPortfolios = useSelector((state) => state.idsDetails.ids);
  const [rows, setRows] = useState([]);
  const [portfolioVehicleListStatus, setPortfolioVehicleListStatus] = useState({});

  function handleDataFromChild(data) {
    setDataFromChild(data);
  }

  const fetchVehicleListsForPortfolio = useCallback(async (portfolioName) => {
    try {
      const response = await ApiManager.getVehicleLists(portfolioName);
      if (Array.isArray(response?.data)) {
        return response.data.length > 0 ? "Added" : "---";
      } else if (Array.isArray(response)) {
        return response.length > 0 ? "Added" : "---";
      } else {
        return "---";
      }
    } catch {
      return "---";
    }
  }, []);

  const handleClose = () => setAnchorEl(null);

  useEffect(() => {
    if (allPortfolios.length > 0) {
      let allRows = [];
      allPortfolios.forEach((portfolio) => {
        allRows.push(createData(portfolio));
      });
      setRows(allRows);
      
      // Directly call the function to avoid dependency issues
      const loadPortfolioStatuses = async () => {
        setLoader(true);
        const statusMap = {};
        
        for (const portfolio of allPortfolios) {
          const status = await fetchVehicleListsForPortfolio(portfolio);
          statusMap[portfolio] = status;
        }
        
        setPortfolioVehicleListStatus(statusMap);
        setLoader(false);
      };
      
      loadPortfolioStatuses();
    }
  }, [allPortfolios, fetchVehicleListsForPortfolio]);

  return (
    <>
      <Topheader />
      {loader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <Box
        sx={{
          marginTop: "3%",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "-webkit-box",
          }}
        >
          <Sidebar 
            sendDataToParent={handleDataFromChild} 
            page="portfolios"
            selectedPortfolio={selectedPortfolio}
          />
          <Box sx={{ flexGrow: 1, marginTop: "16px", marginLeft: "20px" }}>
            <Box
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              width="89%"
            >
              <Typography sx={{ fontSize: "20px", marginTop: "2%" }}>
                {dataFromChild} - Portfolio Settings
              </Typography>
              <Typography sx={{ fontSize: "20px", marginTop: "2%" }}>
                List of Portfolios
              </Typography>
              <Typography
                sx={{ color: "#717273" }}
                variant="subtitle2"
                gutterBottom
              >
                You can have multiple portfolios and respective vehicles list to
                it.
              </Typography>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 700 }} aria-label="customized table">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell align="left" sx={{ width: "50%" }}>
                        Portfolio
                      </StyledTableCell>
                      <StyledTableCell align="left">
                        Vehicle List
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <StyledTableRow key={row.name}>
                        <StyledTableCell
                          sx={{ fontWeight: "bold" }}
                          align="left"
                        >
                          {row.name}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                              justifyContent: "flex-start",
                              width: "100%",
                            }}
                          >
                            {portfolioVehicleListStatus[row.name] || "Loading..."}
                          </Stack>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              slotProps={{
                paper: {
                  sx: {
                    maxWidth: 300,
                    maxHeight: 300,
                    overflowY: "auto",
                    padding: 2,
                  },
                },
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                {popupTitle}
              </Typography>
              {popupContent?.length > 0 ? (
                popupContent.map((country) => (
                  <Typography key={country} sx={{ fontSize: "14px", mb: 0.5 }}>
                    {country}
                  </Typography>
                ))
              ) : (
                <Typography sx={{ fontSize: "14px" }}>N/A</Typography>
              )}
            </Popover>
          </Box>
        </Box>
      </Box>
    </>
  );
}
